import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    ActivityCardType,
    type ActivityCardCreatedBy,
    type ActivityCardData,
    type ActivityTimelineResolverContext,
    type AgentChangeActivityCardData,
    type DetailsChangeActivityCardData,
    type DispositionChangeActivityCardData,
} from "~/modules/activityTimeline/types/activityTimeline.types";
import {extractEventContainerSnapshotFieldChanges} from "~/modules/activityTimeline/resolver/eventContainerLogFieldChangeUtils";
import {toActivityCardCreatedBy} from "~/modules/activityTimeline/utils/activityTimelineCreatedBy";
import {getEventContainerDispositionId} from "~/utilities/ottopilotSemanticColumns";

function toCreatedBy(userId: Uuid | null | undefined): ActivityCardCreatedBy {
    return toActivityCardCreatedBy(userId);
}

function isAgentColumn(columnId: string, agentColumnIds: Array<Uuid>): boolean {
    return agentColumnIds.some((id) => String(id) === columnId);
}

export function resolveFromEventContainerLogs(getAdditionalColumns: CapabilityUdoColumnResolver,
    context: ActivityTimelineResolverContext,
): Array<ActivityCardData> {
    const cards: Array<ActivityCardData> = [];
    const agentColumnIds = context.workspaceSpecific?.agentColumnIds ?? [];
    const remarksColumnId = context.workspaceSpecific?.remarksColumnId ?? null;
    const chronologicalLogs = [...context.eventContainerLogs]
        .filter((log) => log.id === context.eventContainer.id)
        .sort((left, right) => {
            const timestampComparison = left.updatedAt.localeCompare(right.updatedAt);
            return timestampComparison === 0
                ? left.revisionId.localeCompare(right.revisionId)
                : timestampComparison;
        });

    for (const [index, currentLog] of chronologicalLogs.entries()) {
        const previousLog = chronologicalLogs[index - 1];
        if (previousLog === undefined) {
            cards.push({
                cardType: ActivityCardType.detailsChange,
                id: currentLog.revisionId,
                timestamp: currentLog.updatedAt,
                entityLabel: "Lead",
                fieldChanges: [],
                createdBy: toCreatedBy(currentLog.updatedBy),
            });
            continue;
        }

        const remarks =
            remarksColumnId === null
                ? null
                : (currentLog.additionalColumnValues[remarksColumnId] as string | null | undefined) ?? null;

        const previousDispositionId = getEventContainerDispositionId(previousLog, context.eventContainerTemplate);
        const currentDispositionId = getEventContainerDispositionId(currentLog, context.eventContainerTemplate);
        if (currentDispositionId !== previousDispositionId) {
            const dispositionCard: DispositionChangeActivityCardData = {
                cardType: ActivityCardType.dispositionChange,
                id: currentLog.revisionId,
                timestamp: currentLog.updatedAt,
                oldDispositionId: previousDispositionId,
                newDispositionId: currentDispositionId,
                createdBy: toCreatedBy(currentLog.updatedBy),
                remarks,
            };
            cards.push(dispositionCard);
        }

        const fieldChanges = extractEventContainerSnapshotFieldChanges(getAdditionalColumns, previousLog, currentLog, context);
        for (const change of fieldChanges) {
            if (!isAgentColumn(change.fieldKey, agentColumnIds)) {
                continue;
            }
            const agentCard: AgentChangeActivityCardData = {
                cardType: ActivityCardType.agentChange,
                id: `${currentLog.revisionId}-${change.fieldKey}` as Uuid,
                timestamp: currentLog.updatedAt,
                columnId: change.fieldKey as Uuid,
                columnLabel: change.fieldLabel,
                oldValue: change.oldValue,
                newValue: change.newValue,
                createdBy: toCreatedBy(currentLog.updatedBy),
                remarks,
            };
            cards.push(agentCard);
        }

        const detailFieldChanges = fieldChanges.filter(
            (change) => !isAgentColumn(change.fieldKey, agentColumnIds),
        );
        if (detailFieldChanges.length > 0) {
            const detailsCard: DetailsChangeActivityCardData = {
                cardType: ActivityCardType.detailsChange,
                id: currentLog.revisionId,
                timestamp: currentLog.updatedAt,
                entityLabel: "Lead",
                fieldChanges: detailFieldChanges,
                createdBy: toCreatedBy(currentLog.updatedBy),
                remarks,
            };
            cards.push(detailsCard);
        }
    }

    return cards;
}
